import { UseQueryResult, useQuery, useQueryClient } from "react-query";
import { QUERY_KEYS } from "library/shared";
import api from "service/api";
import { IBenefitCategory, IBenefitImplementation } from "library/types";
import useNotificationHandler from "hooks/useNotificationHandler";
import { genUUID } from "library/utils";

type Params = {
  countryCode: number | string | null;
  clientInternalNumberGOS: number | string;
  benefitCategoryTag: number | string;
  benefitImplementationTag: number | string;
  enabled?: boolean;
};

const getBenefitImplementation = async ({
  countryCode,
  clientInternalNumberGOS,
  benefitCategoryTag,
  benefitImplementationTag,
}: Params) => {
  if (
    countryCode === null ||
    clientInternalNumberGOS === null ||
    benefitCategoryTag === null ||
    benefitImplementationTag === null
  )
    return;

  const response = await api.get(
    `/benefitimplementation/${countryCode}/${clientInternalNumberGOS}/${benefitCategoryTag}/${benefitImplementationTag}`
  );

  return response.data;
};

export function useGetBenefitImplementation({
  countryCode,
  clientInternalNumberGOS,
  benefitCategoryTag,
  benefitImplementationTag,
  enabled = true,
}: Params): UseQueryResult<IBenefitImplementation> {
  const { handleAddNotification } = useNotificationHandler();
  return useQuery<IBenefitImplementation, Error>(
    [
      QUERY_KEYS.benefitImplementation,
      countryCode?.toString(),
      clientInternalNumberGOS?.toString(),
      benefitCategoryTag?.toString(),
      benefitImplementationTag?.toString(),
    ],
    () =>
      getBenefitImplementation({
        countryCode,
        clientInternalNumberGOS,
        benefitCategoryTag,
        benefitImplementationTag,
      }),
    {
      enabled: enabled,
      onError: (error: any) => {
        handleAddNotification({
          id: genUUID(),
          text: error.message,
          type: "error",
        });
      },
    }
  );
}

export const useInvalidateBenefitImplementation = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.benefitImplementation,
    });
};
